import { useState, useRef, useEffect } from "react";
import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Shield, ChevronLeft, Loader2, MapPin, Clock, ScanFace,
  UserCheck, Users, CheckCircle2, XCircle, Eye, Send,
  MessageSquare, AlertTriangle, Navigation, Camera,
  Radio, Smartphone, FileText, ShieldCheck, PhoneCall,
  MapPinned, Search, Handshake, ClipboardCheck, Lock,
  Video, ImageIcon,
} from "lucide-react";
import { useLocation } from "wouter";
import { useSocket } from "@/hooks/useSocket";
import CommandNav from "@/components/CommandNav";
import { MapView, type EllaMapController } from "@/components/Map";

const STATUS_STEPS = [
  { key: "detected", label: "Detectado", icon: Eye, description: "Face detectada no dispositivo" },
  { key: "identified", label: "Identificado", icon: UserCheck, description: "Pessoa identificada no banco facial" },
  { key: "classified", label: "Classificado", icon: ScanFace, description: "Classificação atribuída" },
  { key: "agent_dispatched", label: "Agente Acionado", icon: Send, description: "Agente notificado" },
  { key: "agent_en_route", label: "Em Deslocamento", icon: Navigation, description: "Agente a caminho" },
  { key: "agent_on_scene", label: "No Local", icon: MapPinned, description: "Agente chegou ao local" },
  { key: "victim_search", label: "Busca Ativa", icon: Search, description: "Agente buscando a pessoa" },
  { key: "victim_found", label: "Localizada", icon: Camera, description: "Pessoa localizada pelo agente" },
  { key: "approach", label: "Abordagem", icon: Handshake, description: "Contato com a pessoa" },
  { key: "in_progress", label: "Em Atendimento", icon: Clock, description: "Atendimento em andamento" },
  { key: "guidance", label: "Orientação", icon: MessageSquare, description: "Orientação fornecida" },
  { key: "confirmation", label: "Confirmação", icon: ShieldCheck, description: "Situação confirmada" },
  { key: "resolved", label: "Resolvido", icon: CheckCircle2, description: "Ocorrência resolvida" },
  { key: "closed", label: "Encerrado", icon: Lock, description: "Evento encerrado" },
];

const CLASSIFICATION_OPTIONS = [
  { value: "suspect_identified", label: "Suspeito Identificado", color: "text-red-400" },
  { value: "victim_identified", label: "Vítima Identificada", color: "text-amber-400" },
  { value: "monitored_person", label: "Pessoa Monitorada", color: "text-blue-400" },
  { value: "unidentified_person", label: "Não Identificada", color: "text-zinc-400" },
  { value: "under_analysis", label: "Em Análise", color: "text-yellow-400" },
  { value: "confirmed_incident", label: "Incidente Confirmado", color: "text-red-500" },
];

const DISPATCH_STATUS_OPTIONS = [
  { value: "dispatched", label: "Acionado" },
  { value: "en_route", label: "Em Deslocamento" },
  { value: "on_scene", label: "No Local" },
  { value: "resolved", label: "Resolvido" },
  { value: "closed", label: "Encerrado" },
];

const ALL_STATUS_OPTIONS = [
  { value: "detected", label: "Detectado" },
  { value: "identified", label: "Identificado" },
  { value: "classified", label: "Classificado" },
  { value: "agent_dispatched", label: "Agente Acionado" },
  { value: "agent_en_route", label: "Em Deslocamento" },
  { value: "agent_on_scene", label: "No Local" },
  { value: "victim_search", label: "Busca Ativa" },
  { value: "victim_found", label: "Localizada" },
  { value: "approach", label: "Abordagem" },
  { value: "in_progress", label: "Em Atendimento" },
  { value: "guidance", label: "Orientação" },
  { value: "confirmation", label: "Confirmação" },
  { value: "resolved", label: "Resolvido" },
  { value: "closed", label: "Encerrado" },
];

export default function EventDetail() {
  const params = useParams<{ eventId: string }>();
  const eventId = parseInt(params.eventId || "0");
  const [, navigate] = useLocation();

  const { data: event, isLoading, refetch } = trpc.opEvent.get.useQuery({ id: eventId }, { enabled: eventId > 0 });
  const { data: timeline, refetch: refetchTimeline } = trpc.timeline.get.useQuery({ eventId }, { enabled: eventId > 0 });
  const { data: dispatches, refetch: refetchDispatches } = trpc.dispatch.getByEvent.useQuery({ eventId }, { enabled: eventId > 0 });
  const { data: address } = trpc.geo.reverse.useQuery(
    { lat: parseFloat(event?.latitude || "0"), lng: parseFloat(event?.longitude || "0") },
    { enabled: !!event?.latitude && !!event?.longitude, staleTime: 60000 }
  );

  const updateClassification = trpc.opEvent.updateClassification.useMutation({
    onSuccess: () => { refetch(); refetchTimeline(); toast.success("Classificação atualizada"); },
  });
  const updateStatus = trpc.opEvent.updateStatus.useMutation({
    onSuccess: () => { refetch(); refetchTimeline(); toast.success("Status atualizado"); },
  });
  const closeEvent = trpc.opEvent.close.useMutation({
    onSuccess: () => { refetch(); refetchTimeline(); toast.success("Evento encerrado"); },
  });
  const createDispatch = trpc.dispatch.create.useMutation({
    onSuccess: () => { refetch(); refetchDispatches(); refetchTimeline(); toast.success("Agente acionado"); },
  });
  const updateDispatchStatus = trpc.dispatch.updateStatus.useMutation({
    onSuccess: () => { refetch(); refetchDispatches(); refetchTimeline(); toast.success("Status do agente atualizado"); },
  });
  const addNote = trpc.timeline.addNote.useMutation({
    onSuccess: () => { refetchTimeline(); toast.success("Nota adicionada"); },
  });
  const confirmFight = trpc.opEvent.confirmFight.useMutation({
    onSuccess: () => { refetch(); refetchTimeline(); toast.success("Classificação de briga atualizada"); },
  });
  const addInvolvedPerson = trpc.opEvent.addInvolvedPerson.useMutation({
    onSuccess: () => { refetch(); refetchTimeline(); toast.success("Envolvido adicionado"); },
  });
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [newPersonName, setNewPersonName] = useState("");
  const [newPersonRole, setNewPersonRole] = useState<"aggressor" | "victim" | "witness" | "unknown">("unknown");
  const [newPersonNotes, setNewPersonNotes] = useState("");

  // V7.5: Agent camera live preview + location
  const { socket } = useSocket();
  const [agentCameraFrame, setAgentCameraFrame] = useState<string | null>(null);
  const [agentCameraActive, setAgentCameraActive] = useState(false);
  const [showAgentCamera, setShowAgentCamera] = useState(false);
  const [agentLocation, setAgentLocation] = useState<{ lat: number; lng: number; timestamp: string } | null>(null);
  const cameraTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // V14 Event Workspace: victim realtime, AI, GPS, messages and evidence stay bound to eventId.
  const [activeVictims, setActiveVictims] = useState<any[]>([]);
  const [victimCameraFrame, setVictimCameraFrame] = useState<string | null>(null);
  const [victimCameraStatus, setVictimCameraStatus] = useState<string>("offline");
  const [victimLocation, setVictimLocation] = useState<{lat:number;lng:number;timestamp:number}|null>(null);
  const [sceneAnalysis, setSceneAnalysis] = useState<any>(null);
  const [sceneEscalations, setSceneEscalations] = useState<string[]>([]);
  const [victimImageUrl, setVictimImageUrl] = useState<string | null>(null);
  const [victimMessage, setVictimMessage] = useState("");
  const [agentMessage, setAgentMessage] = useState("");
  const [realtimeLog, setRealtimeLog] = useState<Array<{at:number;kind:string;text:string}>>([]);
  const eventMapRef = useRef<EllaMapController|null>(null);
  const addRealtime = (kind:string,text:string) => setRealtimeLog(prev => [{at:Date.now(),kind,text}, ...prev].slice(0,80));
  const linkedVictim = activeVictims.find(v => Number(v.eventId) === eventId) || null;

  const requestVictimCamera = () => {
    if (!socket || !linkedVictim) return toast.error("Vítima não está conectada a este evento");
    socket.emit("dashboard:camera-request", { socketId: linkedVictim.socketId, clientId: linkedVictim.clientId, withAudio: true });
    setVictimCameraStatus("requested");
    addRealtime("camera", "Central solicitou câmera e áudio da vítima");
  };
  const sendVictimMessage = (message?:string) => {
    const text=(message ?? victimMessage).trim();
    if (!socket || !linkedVictim || !text) return;
    socket.emit("dashboard:message-to-victim", { socketId: linkedVictim.socketId, clientId: linkedVictim.clientId, eventId, message:text });
    setVictimMessage(""); addRealtime("message", `Central → vítima: ${text}`);
  };
  const sendAgentMessage = (message?:string) => {
    const text=(message ?? agentMessage).trim(); if(!socket || !text) return;
    socket.emit("dashboard:message-to-agent", { eventId, message:text, kind:"message" });
    setAgentMessage(""); addRealtime("message", `Central → agente: ${text}`);
  };

  // V7.5: Listen for agent camera frames and location for THIS event
  useEffect(() => {
    if (!socket || !eventId) return;
    const handleFrame = (data: any) => {
      if (data.eventId !== eventId) return;
      setAgentCameraFrame(data.frameBase64);
      setAgentCameraActive(true);
      if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);
      cameraTimeoutRef.current = setTimeout(() => setAgentCameraActive(false), 10000);
    };
    const handleLocation = (data: any) => {
      if (data.eventId !== eventId) return;
      setAgentLocation({ lat: data.latitude, lng: data.longitude, timestamp: data.timestamp });
    };
    const handleCameraStatus = (data: any) => {
      if (data.eventId !== eventId) return;
      if (data.status === "active") setAgentCameraActive(true);
      else if (data.status === "stopped") { setAgentCameraActive(false); setAgentCameraFrame(null); }
    };
    socket.on("agent:camera-frame-relay", handleFrame);
    socket.on("agent:location-relay", handleLocation);
    socket.on("agent:camera-status", handleCameraStatus);
    return () => {
      socket.off("agent:camera-frame-relay", handleFrame);
      socket.off("agent:location-relay", handleLocation);
      socket.off("agent:camera-status", handleCameraStatus);
      if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);
    };
  }, [socket, eventId]);

  useEffect(() => {
    if (!socket || !eventId) return;
    const active=(payload:any)=>setActiveVictims(Array.isArray(payload?.victims)?payload.victims:[]);
    const victimFrame=(data:any)=>{ if(Number(data.eventId)!==eventId)return; setVictimCameraFrame(data.frame); setVictimCameraStatus("live"); addRealtime("camera",`Frame recebido de ${data.reportedBy||"vítima"}`); };
    const camStatus=(data:any)=>{ if(linkedVictim && data.socketId!==linkedVictim.socketId)return; setVictimCameraStatus(data.status||"unknown"); addRealtime("camera",`Câmera da vítima: ${data.status||"status"}`); };
    const loc=(data:any)=>{ if(Number(data.eventId)!==eventId)return; const pos={lat:Number(data.lat),lng:Number(data.lng),timestamp:Date.now()}; setVictimLocation(pos); eventMapRef.current?.setEmergency(pos,"Vítima / ocorrência"); eventMapRef.current?.appendTrail(pos); };
    const analysis=(data:any)=>{ if(Number(data.eventId)!==eventId)return; setSceneAnalysis(data.analysis); addRealtime("ai",`IA ${data.analysis?.priority||""}: ${data.analysis?.situation||"análise atualizada"}`); };
    const escalation=(data:any)=>{ if(Number(data.eventId)!==eventId)return; setSceneEscalations(data.escalations||[]); addRealtime("alert",`Escalada IA: ${(data.escalations||[]).join(", ")}`); };
    const image=(data:any)=>{ if(Number(data.eventId)!==eventId)return; setVictimImageUrl(data.imageUrl); addRealtime("evidence","Foto da vítima registrada"); };
    const agentMsg=(data:any)=>{ if(Number(data.eventId)!==eventId)return; addRealtime("message",`${data.agentName||"Agente"} → Central: ${data.message||data.signal||"atualização"}`); };
    // Event workspace is an operator surface. Join dashboard role even for mobile events that have no DB sessionId.
    socket.emit("dashboard:join", { sessionId: Number(event?.sessionId || 0) });
    socket.on("victim:active-update",active); socket.on("mobile:emergency-camera-feed",victimFrame); socket.on("dashboard:mobile-camera-status",camStatus);
    socket.on("mobile:emergency-location",loc); socket.on("scene:analysis-result",analysis); socket.on("scene:escalation",escalation);
    socket.on("mobile:victim-image-saved",image); socket.on("agent:message-to-dashboard",agentMsg);
    socket.emit("get-active-victims");
    return()=>{ socket.off("victim:active-update",active); socket.off("mobile:emergency-camera-feed",victimFrame); socket.off("dashboard:mobile-camera-status",camStatus); socket.off("mobile:emergency-location",loc); socket.off("scene:analysis-result",analysis); socket.off("scene:escalation",escalation); socket.off("mobile:victim-image-saved",image); socket.off("agent:message-to-dashboard",agentMsg); };
  },[socket,eventId,event?.sessionId,linkedVictim?.socketId]);

  useEffect(()=>{ if(agentLocation&&eventMapRef.current){eventMapRef.current.setAgent({lat:agentLocation.lat,lng:agentLocation.lng},"Agente");eventMapRef.current.fitOperationalBounds();}},[agentLocation?.lat,agentLocation?.lng]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ella-purple-light" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Evento não encontrado</h3>
          <Button variant="outline" onClick={() => navigate("/events")}>Voltar</Button>
        </div>
      </div>
    );
  }

  const currentStepIndex = STATUS_STEPS.findIndex(s => s.key === event.status);
  const effectiveStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header — ELLA Command Platform */}
      <div className="sticky top-0 z-50">
        <CommandNav
          pageTitle={`Evento #${event.id}`}
          rightContent={
            event.status !== "closed" ? (
              <div className="flex items-center gap-2">
                <Select
                  value={event.status || "detected"}
                  onValueChange={(val) => updateStatus.mutate({ id: eventId, status: val as any })}
                >
                  <SelectTrigger className="h-7 text-xs bg-white/[0.03] border-white/10 text-white/70 w-44">
                    <SelectValue placeholder="Avançar status" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline" size="sm"
                  className="text-xs h-7 text-red-400 border-red-400/30 hover:bg-red-400/10"
                  onClick={() => closeEvent.mutate({ id: eventId, notes: "Encerrado pelo operador" })}
                >
                  <XCircle className="w-3 h-3 mr-1" /> Encerrar
                </Button>
              </div>
            ) : undefined
          }
        />
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Involved Persons Summary */}
        <div className="bg-card border border-border/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Users className="w-3.5 h-3.5" />
              Envolvidos ({event.person2Name ? 2 : event.personName ? 1 : 0})
            </h3>
            {event.person2Name && (
              <Badge className="text-[10px] bg-red-500/20 text-red-400 border-red-500/30 animate-pulse">
                CONFLITO
              </Badge>
            )}
          </div>
          <div className="flex gap-4">
            {/* Person 1 */}
            {event.personName && (
              <div className="flex items-center gap-3 p-2 rounded-lg bg-background border border-border/30 flex-1">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary shrink-0">
                  {event.capturedImageUrl ? (
                    <img src={event.capturedImageUrl} alt="P1" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ScanFace className="w-5 h-5 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{event.personName}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {event.personCategory === 'restricted' ? 'Possível Agressor' :
                     event.personCategory === 'flagged' ? 'Monitorado' :
                     event.person2Name ? 'Possível Vítima' : event.personCategory || 'Identificado'}
                  </p>
                </div>
                {event.confidenceScore && (
                  <Badge variant="outline" className="text-[9px] ml-auto shrink-0">
                    {parseFloat(event.confidenceScore).toFixed(0)}%
                  </Badge>
                )}
              </div>
            )}
            {/* Person 2 */}
            {event.person2Name && (
              <div className="flex items-center gap-3 p-2 rounded-lg bg-red-900/10 border border-red-500/20 flex-1">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary shrink-0">
                  {event.person2CapturedImageUrl ? (
                    <img src={event.person2CapturedImageUrl} alt="P2" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ScanFace className="w-5 h-5 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{event.person2Name}</p>
                  <p className="text-[10px] text-red-400">
                    {event.person2Category === 'restricted' ? 'Possível Agressor' :
                     event.person2Category === 'flagged' ? 'Monitorado' : 'Possível Vítima'}
                  </p>
                </div>
                {event.person2Confidence && (
                  <Badge variant="outline" className="text-[9px] ml-auto shrink-0 border-red-500/30 text-red-400">
                    {parseFloat(event.person2Confidence).toFixed(0)}%
                  </Badge>
                )}
              </div>
            )}
            {/* No person identified */}
            {!event.personName && (
              <div className="flex items-center gap-3 p-2 rounded-lg bg-background border border-border/30 flex-1">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Users className="w-5 h-5 text-muted-foreground/30" />
                </div>
                <p className="text-xs text-muted-foreground">Nenhum envolvido identificado</p>
              </div>
            )}
          </div>
        </div>

        {/* V14 — Event Workspace: one operational surface for camera, AI, GPS, evidence and communications */}
        <div className="bg-card border border-purple-500/30 rounded-xl p-4 mb-6 relative z-20">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div><h2 className="text-sm font-bold">Central de Atendimento — Evento #{eventId}</h2><p className="text-[10px] text-muted-foreground">Câmeras, IA, GPS, evidências, vítima e agente vinculados ao mesmo evento.</p></div>
            <Badge className={linkedVictim?"bg-green-500/20 text-green-300":"bg-zinc-500/20 text-zinc-300"}>{linkedVictim?"VÍTIMA ONLINE":"VÍTIMA OFFLINE"}</Badge>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="rounded-lg border border-border/50 bg-black overflow-hidden relative z-30">
                <div className="px-3 py-2 flex items-center justify-between bg-white/5"><span className="text-xs font-semibold">Câmera da vítima</span><Badge variant="outline" className="text-[9px]">{victimCameraStatus.toUpperCase()}</Badge></div>
                {victimCameraFrame?<img src={victimCameraFrame} className="w-full h-64 object-contain bg-black" alt="Câmera vítima"/>:<div className="h-64 flex items-center justify-center text-xs text-white/40">Aguardando stream autorizado</div>}
                <div className="p-2 flex gap-2"><Button size="sm" onClick={requestVictimCamera} disabled={!linkedVictim}><Camera className="w-3.5 h-3.5 mr-1"/>Solicitar câmera + áudio</Button></div>
              </div>
              <div className="rounded-lg border border-border/50 overflow-hidden relative z-10">
                <div className="px-3 py-2 bg-card text-xs font-semibold">Mapa operacional — vítima + agente</div>
                <MapView className="h-64" initialCenter={victimLocation?{lat:victimLocation.lat,lng:victimLocation.lng}:event.latitude&&event.longitude?{lat:Number(event.latitude),lng:Number(event.longitude)}:undefined} initialZoom={15} liveCenter={victimLocation} onMapReady={(m)=>{eventMapRef.current=m;if(event.latitude&&event.longitude)m.setEmergency({lat:Number(event.latitude),lng:Number(event.longitude)},"Ocorrência");if(agentLocation)m.setAgent({lat:agentLocation.lat,lng:agentLocation.lng},"Agente");m.fitOperationalBounds();}}/>
              </div>
            </div>
            <div className="space-y-3">
              <div className="rounded-lg border border-border/50 p-3">
                <div className="flex justify-between"><span className="text-xs font-semibold">IA operacional</span><Badge variant="outline" className="text-[9px]">{sceneAnalysis?.priority?.toUpperCase()||"AGUARDANDO"}</Badge></div>
                <p className="text-xs mt-2">{sceneAnalysis?.situation||"A IA passa a acompanhar os frames assim que uma câmera autorizada transmitir."}</p>
                {sceneAnalysis?.recommendation&&<p className="text-[10px] text-amber-300 mt-2">Recomendação: {sceneAnalysis.recommendation}</p>}
                {sceneEscalations.includes("medical")||sceneEscalations.includes("fire")?<div className="mt-2 p-2 rounded bg-red-500/15 border border-red-500/30 text-xs text-red-200"><PhoneCall className="inline w-3.5 h-3.5 mr-1"/>Possível necessidade médica/bombeiros — requer confirmação e acionamento do operador.</div>:null}
              </div>
              <div className="rounded-lg border border-border/50 p-3">
                <span className="text-xs font-semibold">Evidências / identificação</span>
                <div className="grid grid-cols-3 gap-2 mt-2">{[victimImageUrl,event.capturedImageUrl,event.person2CapturedImageUrl].filter(Boolean).map((u:any,i)=><img key={i} src={u} className="w-full h-20 object-cover rounded border border-border/40"/> )}</div>
                <p className="text-[10px] text-muted-foreground mt-2">Correspondências faciais são candidatas e exigem confirmação humana antes de vincular identidade/papel.</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3 space-y-2"><span className="text-xs font-semibold">Conversar com a vítima</span><div className="flex gap-2"><Input value={victimMessage} onChange={e=>setVictimMessage(e.target.value)} placeholder="Mensagem para vítima"/><Button onClick={()=>sendVictimMessage()} disabled={!linkedVictim}>Enviar</Button></div><div className="flex flex-wrap gap-1">{["Estamos acompanhando você.","Permaneça em local seguro.","Um agente foi acionado.","Compartilhe sua localização.","Você consegue falar? Responda SIM ou NÃO."].map(t=><button key={t} onClick={()=>sendVictimMessage(t)} className="text-[9px] px-2 py-1 rounded border border-border/50">{t}</button>)}</div></div>
              <div className="rounded-lg border border-border/50 p-3 space-y-2"><span className="text-xs font-semibold">Conversar com o agente</span><div className="flex gap-2"><Input value={agentMessage} onChange={e=>setAgentMessage(e.target.value)} placeholder="Mensagem para agente"/><Button onClick={()=>sendAgentMessage()}>Enviar</Button></div></div>
              <div className="rounded-lg border border-border/50 p-3 max-h-40 overflow-auto"><span className="text-xs font-semibold">Realtime deste evento</span>{realtimeLog.length===0?<p className="text-[10px] text-muted-foreground mt-2">Aguardando atualizações...</p>:realtimeLog.map((x,i)=><div key={i} className="text-[10px] py-1 border-b border-border/20"><span className="text-muted-foreground">{new Date(x.at).toLocaleTimeString("pt-BR")}</span> · {x.text}</div>)}</div>
            </div>
          </div>
        </div>

        {/* Status Stepper - Compact 14-step */}
        <div className="bg-card border border-border/50 rounded-lg p-4 mb-6 overflow-x-auto">
          <h3 className="text-[10px] font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Fluxo Operacional</h3>
          <div className="flex items-center min-w-[900px]">
            {STATUS_STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isActive = i === effectiveStepIndex;
              const isComplete = i < effectiveStepIndex;

              return (
                <div key={step.key} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                      isComplete ? "bg-green-500/20 border-green-500 text-green-400" :
                      isActive ? "ella-gradient border-transparent text-white ella-glow" :
                      "bg-secondary border-border/50 text-muted-foreground/30"
                    }`}>
                      {isComplete ? <CheckCircle2 className="w-3.5 h-3.5" /> : <StepIcon className="w-3.5 h-3.5" />}
                    </div>
                    <span className={`text-[8px] mt-1 font-medium text-center leading-tight max-w-[60px] ${
                      isActive ? "text-ella-purple-light" : isComplete ? "text-green-400" : "text-muted-foreground/30"
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {i < STATUS_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 mt-[-14px] ${
                      isComplete ? "bg-green-500/50" : "bg-border/20"
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Event Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Info */}
            <div className="bg-card border border-border/50 rounded-lg p-5">
              <h3 className="text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Detalhes do Evento</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoField label="ID" value={`#${event.id}`} />
                <InfoField label="Tipo" value={
                  event.eventType === "facial_recognition" ? "Reconhecimento Facial" :
                  event.eventType === "manual_report" ? "Relato Manual" :
                  event.eventType === "alert" ? "Alerta" :
                  event.eventType === "incident" ? "Incidente" : "Ronda"
                } />
                <InfoField label="Horário" value={event.createdAt ? new Date(event.createdAt).toLocaleString("pt-BR") : "N/A"} />
                <InfoField label="Setor" value={event.sector || "Não definido"} />
                <InfoField label="Sessão" value={event.sessionId ? `#${event.sessionId}` : "N/A"} />
                {event.confidenceScore && (
                  <InfoField label="Confiança" value={`${parseFloat(event.confidenceScore).toFixed(1)}%`} highlight />
                )}
              </div>

              {/* Address from geocoding */}
              {address?.address && (
                <div className="mt-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <div className="flex items-start gap-2">
                    <Navigation className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-purple-200">{address.address}</p>
                      {address.sector && <p className="text-[10px] text-purple-300/60 mt-0.5">Setor: {address.sector}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* GPS Coordinates */}
              {event.latitude && event.longitude && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="p-2 rounded bg-secondary/50">
                    <div className="text-[9px] text-muted-foreground">Latitude</div>
                    <div className="text-[10px] font-mono font-medium">{event.latitude}</div>
                  </div>
                  <div className="p-2 rounded bg-secondary/50">
                    <div className="text-[9px] text-muted-foreground">Longitude</div>
                    <div className="text-[10px] font-mono font-medium">{event.longitude}</div>
                  </div>
                  <div className="p-2 rounded bg-secondary/50">
                    <div className="text-[9px] text-muted-foreground">Precisão</div>
                    <div className="text-[10px] font-mono font-medium">{event.accuracy ? `${parseFloat(event.accuracy).toFixed(0)}m` : "N/A"}</div>
                  </div>
                </div>
              )}

              {/* Classification Selector */}
              <div className="mt-4 pt-4 border-t border-border/30">
                <Label className="text-xs text-muted-foreground">Classificação</Label>
                <Select
                  value={event.classification || "under_analysis"}
                  onValueChange={(val) => updateClassification.mutate({ id: eventId, classification: val as any })}
                  disabled={event.status === "closed"}
                >
                  <SelectTrigger className="mt-1 bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className={opt.color}>{opt.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Images Comparison */}
            <div className="bg-card border border-border/50 rounded-lg p-5">
              <h3 className="text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Identificação Visual</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-2">Imagem Capturada</p>
                  <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border/50">
                    {event.capturedImageUrl ? (
                      <img src={event.capturedImageUrl} alt="Captura" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ScanFace className="w-12 h-12 text-muted-foreground/20" />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-2">Foto Cadastrada</p>
                  <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border/50">
                    {event.personPhotoUrl ? (
                      <img src={event.personPhotoUrl} alt="Cadastrada" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <Users className="w-12 h-12 text-muted-foreground/20 mb-2" />
                        <span className="text-xs text-muted-foreground">{event.personName || "Não identificado"}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Person info below photos */}
              {event.personName && (
                <div className="mt-3 p-3 rounded-lg bg-background border border-border/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold">{event.personName}</span>
                      {event.personRole && <span className="text-xs text-muted-foreground ml-2">{event.personRole}</span>}
                    </div>
                    {event.confidenceScore && (
                      <Badge className={`text-[10px] ${
                        parseFloat(event.confidenceScore) > 60 ? "bg-green-500/20 text-green-400 border-green-500/30" :
                        parseFloat(event.confidenceScore) > 40 ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" :
                        "bg-orange-500/20 text-orange-400 border-orange-500/30"
                      }`}>
                        {parseFloat(event.confidenceScore) > 60 ? "Alta Confiança" :
                         parseFloat(event.confidenceScore) > 40 ? "Média Confiança" : "Requer Validação"}
                        {" — "}{parseFloat(event.confidenceScore).toFixed(0)}%
                      </Badge>
                    )}
                  </div>
                  {event.personCategory && <p className="text-[10px] text-muted-foreground mt-1">Categoria: {event.personCategory}</p>}
                </div>
              )}
            </div>

            {/* Person 2 - Conflict */}
            {event.person2Name && (
              <div className="bg-card border border-red-500/30 rounded-lg p-5">
                <h3 className="text-xs font-semibold text-red-400 mb-4 uppercase tracking-wider">2\u00aa Pessoa Envolvida (Conflito)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-2">Imagem Capturada</p>
                    <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-red-500/30">
                      {event.person2CapturedImageUrl ? (
                        <img src={event.person2CapturedImageUrl} alt="Captura P2" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ScanFace className="w-12 h-12 text-muted-foreground/20" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-2">Foto Cadastrada</p>
                    <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-red-500/30">
                      {event.person2PhotoUrl ? (
                        <img src={event.person2PhotoUrl} alt="Cadastrada P2" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          <Users className="w-12 h-12 text-muted-foreground/20 mb-2" />
                          <span className="text-xs text-muted-foreground">{event.person2Name || "Não identificado"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 p-3 rounded-lg bg-red-900/10 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold">{event.person2Name}</span>
                      <span className="text-xs text-red-400 ml-2 font-medium">
                        {event.person2Category === "restricted" ? "Agressor" : "Vítima"}
                      </span>
                    </div>
                    {event.person2Confidence && (
                      <Badge className="text-[10px] bg-red-500/20 text-red-400 border-red-500/30">
                        {parseFloat(event.person2Confidence).toFixed(0)}%
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Fight / Multiple Persons Section */}
            {event.person2Name && (
              <div className="bg-card border border-amber-500/30 rounded-lg p-5">
                <h3 className="text-xs font-semibold text-amber-400 mb-4 uppercase tracking-wider flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Análise de Conflito
                </h3>
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-amber-900/10 border border-amber-500/20">
                    <p className="text-xs font-medium text-amber-300 mb-1">Classificação Automática</p>
                    <p className="text-[11px] text-muted-foreground">
                      O sistema detectou <span className="text-white font-medium">2 pessoas</span> na mesma cena.
                      {event.personCategory === 'restricted' || event.person2Category === 'restricted' ? (
                        <> Uma delas possui registro de <span className="text-red-400 font-medium">restrição</span> no banco facial, sendo classificada como <span className="text-red-400">possível agressor</span>.  A outra foi classificada como <span className="text-green-400">possível vítima</span>.</>
                      ) : (
                        <> Nenhuma possui restrição no banco facial. A classificação foi baseada na categoria cadastrada e requer <span className="text-amber-400 font-medium">validação manual</span> pelo operador.</>
                      )}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 rounded bg-secondary/50">
                      <div className="text-[9px] text-muted-foreground">Tipo de Incidente</div>
                      <div className="text-xs font-medium mt-0.5">
                        {event.classification === 'confirmed_incident' ? 'Briga Confirmada' : 'Conflito Detectado'}
                      </div>
                    </div>
                    <div className="p-2 rounded bg-secondary/50">
                      <div className="text-[9px] text-muted-foreground">Validação</div>
                      <div className="text-xs font-medium mt-0.5">
                        {event.classification === 'confirmed_incident' ? (
                          <span className="text-red-400">Confirmado pelo Operador</span>
                        ) : (
                          <span className="text-amber-400">Pendente de Validação</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {event.status !== 'closed' && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm" variant="outline"
                        className="text-[10px] h-7 border-red-500/30 text-red-400 hover:bg-red-500/10"
                        onClick={() => confirmFight.mutate({ id: eventId, confirmed: true })}
                        disabled={confirmFight.isPending}
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" /> Confirmar Briga
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="text-[10px] h-7 border-green-500/30 text-green-400 hover:bg-green-500/10"
                        onClick={() => confirmFight.mutate({ id: eventId, confirmed: false })}
                        disabled={confirmFight.isPending}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Não é Briga
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="text-[10px] h-7 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        onClick={() => setShowAddPerson(true)}
                      >
                        <Users className="w-3 h-3 mr-1" /> Adicionar Envolvido
                      </Button>
                    </div>
                  )}

                  {/* Additional Involved Persons */}
                  {(() => {
                    let analysis: any = {};
                    try { analysis = event.conflictAnalysis ? JSON.parse(event.conflictAnalysis as string) : {}; } catch {}
                    const additionalPersons = analysis.additionalPersons || [];
                    if (additionalPersons.length === 0) return null;
                    return (
                      <div className="mt-3 pt-3 border-t border-amber-500/20">
                        <p className="text-[10px] text-amber-300 font-medium mb-2">Envolvidos Adicionais ({additionalPersons.length})</p>
                        <div className="space-y-1.5">
                          {additionalPersons.map((p: any, i: number) => (
                            <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-secondary/30">
                              <Users className="w-3 h-3 text-muted-foreground" />
                              <span className="text-[11px] font-medium">{p.name}</span>
                              <Badge variant="outline" className={`text-[9px] ${
                                p.role === 'aggressor' ? 'border-red-500/30 text-red-400' :
                                p.role === 'victim' ? 'border-green-500/30 text-green-400' :
                                p.role === 'witness' ? 'border-blue-500/30 text-blue-400' :
                                'border-zinc-500/30 text-zinc-400'
                              }`}>
                                {p.role === 'aggressor' ? 'Agressor' : p.role === 'victim' ? 'Vítima' : p.role === 'witness' ? 'Testemunha' : 'Desconhecido'}
                              </Badge>
                              {p.notes && <span className="text-[9px] text-muted-foreground">{p.notes}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Add Involved Person Dialog */}
                  {showAddPerson && (
                    <div className="mt-3 p-3 rounded-lg bg-secondary/30 border border-blue-500/20">
                      <p className="text-[10px] font-medium text-blue-400 mb-2">Adicionar Envolvido</p>
                      <div className="space-y-2">
                        <Input
                          placeholder="Nome do envolvido"
                          value={newPersonName}
                          onChange={(e) => setNewPersonName(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <Select value={newPersonRole} onValueChange={(v) => setNewPersonRole(v as any)}>
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="aggressor">Agressor</SelectItem>
                            <SelectItem value="victim">Vítima</SelectItem>
                            <SelectItem value="witness">Testemunha</SelectItem>
                            <SelectItem value="unknown">Desconhecido</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Observações (opcional)"
                          value={newPersonNotes}
                          onChange={(e) => setNewPersonNotes(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm" className="h-7 text-[10px]"
                            disabled={!newPersonName.trim() || addInvolvedPerson.isPending}
                            onClick={() => {
                              addInvolvedPerson.mutate({
                                eventId,
                                personName: newPersonName.trim(),
                                personRole: newPersonRole,
                                notes: newPersonNotes.trim() || undefined,
                              });
                              setNewPersonName("");
                              setNewPersonRole("unknown");
                              setNewPersonNotes("");
                              setShowAddPerson(false);
                            }}
                          >
                            {addInvolvedPerson.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Salvar"}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setShowAddPerson(false)}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Origin & Evidence */}
            <div className="bg-card border border-border/50 rounded-lg p-5">
              <h3 className="text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Origem e Evidências</h3>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <InfoField label="Origem" value={event.origin || "Detecção automática"} />
                <InfoField label="Dispositivo" value={event.deviceName || "N/A"} />
                <InfoField label="Navegador" value={event.deviceBrowser || "N/A"} />
                <InfoField label="Código Sessão" value={event.sessionCode || (event.sessionId ? `#${event.sessionId}` : "N/A")} />
              </div>
              {event.evidenceNotes && (
                <div className="p-2 rounded bg-secondary/50 mt-2">
                  <span className="text-[10px] text-muted-foreground">Notas de Evidência</span>
                  <p className="text-xs mt-1">{event.evidenceNotes}</p>
                </div>
              )}
            </div>

            {/* Event Images Gallery */}
            <EventImagesGallery eventId={eventId} />

            {/* Agent Dispatch */}
            <div className="bg-card border border-border/50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Acionamento de Agente</h3>
                {event.status !== "closed" && (
                  <DispatchDialog eventId={eventId} onDispatch={(name, notes) => createDispatch.mutate({ eventId, agentName: name, notes })} />
                )}
              </div>

              {dispatches && dispatches.length > 0 ? (
                <div className="space-y-3">
                  {dispatches.map((d) => (
                    <div key={d.id} className="p-3 rounded-lg bg-background border border-border/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-amber-400" />
                          </div>
                          <div>
                            <span className="text-sm font-medium">{d.agentName}</span>
                            <p className="text-[10px] text-muted-foreground">
                              {d.createdAt ? new Date(d.createdAt).toLocaleString("pt-BR") : ""}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                          d.status === "resolved" || d.status === "closed" ? "border-green-500/30 text-green-400" :
                          d.status === "on_scene" ? "border-amber-500/30 text-amber-400" :
                          d.status === "en_route" ? "border-blue-500/30 text-blue-400" :
                          "border-purple-500/30 text-purple-400"
                        }`}>
                          {d.status === "pending" ? "Aguardando" :
                           d.status === "dispatched" ? "Acionado" :
                           d.status === "en_route" ? "Em Deslocamento" :
                           d.status === "on_scene" ? "No Local" :
                           d.status === "resolved" ? "Resolvido" : "Encerrado"}
                        </Badge>
                      </div>
                      {d.notes && <p className="text-xs text-muted-foreground mb-2">{d.notes}</p>}
                      {event.status !== "closed" && d.status !== "closed" && d.status !== "resolved" && (
                        <Select
                          value={d.status || "dispatched"}
                          onValueChange={(val) => updateDispatchStatus.mutate({ id: d.id, eventId, status: val as any })}
                        >
                          <SelectTrigger className="h-7 text-xs bg-background">
                            <SelectValue placeholder="Atualizar status" />
                          </SelectTrigger>
                          <SelectContent>
                            {DISPATCH_STATUS_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Users className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Nenhum agente acionado</p>
                </div>
              )}

              {/* V7.5: Agent location display */}
              {agentLocation && (
                <div className="mt-3 p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-wider">Localização do Agente</span>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground">
                    {agentLocation.lat.toFixed(5)}, {agentLocation.lng.toFixed(5)}
                  </div>
                  <div className="text-[9px] text-muted-foreground/60 mt-0.5">
                    Atualizado {new Date(agentLocation.timestamp).toLocaleTimeString('pt-BR')}
                  </div>
                </div>
              )}

              {/* V7.5: Agent camera live preview button */}
              {agentCameraActive && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowAgentCamera(true)}
                    className="w-full p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/25 hover:bg-cyan-500/20 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-medium text-cyan-400">Câmera do Agente</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-[10px] text-red-400 font-semibold">AO VIVO</span>
                      </div>
                    </div>
                    {agentCameraFrame && (
                      <div className="mt-2 rounded overflow-hidden border border-border/30">
                        <img src={agentCameraFrame} alt="Preview" className="w-full h-24 object-cover" />
                      </div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Timeline */}
          <div className="space-y-6">
            {/* Add Note */}
            {event.status !== "closed" && (
              <NoteForm onSubmit={(desc) => addNote.mutate({ eventId, description: desc })} loading={addNote.isPending} />
            )}

            {/* Timeline */}
            <div className="bg-card border border-border/50 rounded-lg p-5">
              <h3 className="text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Linha do Tempo</h3>
              {timeline && timeline.length > 0 ? (
                <div className="space-y-0">
                  {timeline.map((entry, i) => {
                    const actionLabel = (a: string) => {
                      const map: Record<string, string> = {
                        event_created: "Evento Criado",
                        classification_updated: "Classificação",
                        status_updated: "Status Atualizado",
                        agent_dispatched: "Agente Acionado",
                        dispatch_status_updated: "Status Agente",
                        note_added: "Tratativa",
                        event_closed: "Encerrado",
                        victim_validated: "Vítima Validada",
                        agent_confirmed: "Agente Confirmou",
                        victim_found: "Pessoa Localizada",
                        approach_started: "Abordagem Iniciada",
                        guidance_given: "Orientação",
                        safety_confirmed: "Segurança Confirmada",
                        auto_dispatch: "Acionamento Automático",
                      };
                      return map[a] || a;
                    };

                    const actionColor = (a: string) => {
                      if (a.includes("closed") || a.includes("resolved")) return "bg-green-500";
                      if (a.includes("agent") || a.includes("dispatch")) return "bg-amber-500";
                      if (a.includes("victim") || a.includes("found")) return "bg-blue-500";
                      if (a.includes("created") || a.includes("detected")) return "bg-purple-500";
                      return "bg-border/50";
                    };

                    return (
                      <div key={entry.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full shrink-0 ${
                            i === 0 ? "ella-gradient" : actionColor(entry.action || "")
                          }`} />
                          {i < timeline.length - 1 && <div className="w-px flex-1 bg-border/30 my-1" />}
                        </div>
                        <div className="pb-4 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] font-medium">{actionLabel(entry.action || "")}</span>
                            <span className="text-[9px] text-muted-foreground/50">{entry.performedBy}</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">{entry.description}</p>
                          <span className="text-[9px] text-muted-foreground/40 mt-0.5 block">
                            {entry.createdAt ? new Date(entry.createdAt).toLocaleString("pt-BR") : ""}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4">Nenhum registro na timeline</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* V7.5: Agent Camera Live Modal */}
      {showAgentCamera && agentCameraFrame && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-semibold text-white">Câmera do Agente</span>
                <div className="flex items-center gap-1 ml-2 px-2 py-0.5 rounded bg-red-500/20 border border-red-500/30">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[10px] text-red-400 font-bold">AO VIVO</span>
                </div>
              </div>
              <button
                onClick={() => setShowAgentCamera(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <XCircle className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="rounded-lg overflow-hidden border border-border/30">
              <img src={agentCameraFrame} alt="Câmera ao vivo" className="w-full object-contain" />
            </div>
            {agentLocation && (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3 text-cyan-400" />
                <span className="font-mono">{agentLocation.lat.toFixed(5)}, {agentLocation.lng.toFixed(5)}</span>
                <span className="text-muted-foreground/50">• {new Date(agentLocation.timestamp).toLocaleTimeString('pt-BR')}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <p className={`text-sm font-medium ${highlight ? "text-ella-purple-light" : ""}`}>{value}</p>
    </div>
  );
}

function DispatchDialog({ eventId, onDispatch }: { eventId: number; onDispatch: (name: string, notes?: string) => void }) {
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="ella-gradient text-white border-0 text-xs h-7 px-3">
          <Send className="w-3 h-3 mr-1" /> Acionar Agente
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card border-border/50">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Acionar Agente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-muted-foreground">Nome do Agente *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do agente" className="mt-1 bg-background" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Observações</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instruções ou observações..." className="mt-1 bg-background min-h-[60px]" />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="text-xs">Cancelar</Button>
          </DialogClose>
          <Button
            className="ella-gradient text-white border-0 text-xs"
            onClick={() => { if (!name.trim()) { toast.error("Nome é obrigatório"); return; } onDispatch(name, notes || undefined); setOpen(false); setName(""); setNotes(""); }}
          >
            <Send className="w-3 h-3 mr-1" /> Acionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoteForm({ onSubmit, loading }: { onSubmit: (desc: string) => void; loading: boolean }) {
  const [note, setNote] = useState("");

  return (
    <div className="bg-card border border-border/50 rounded-lg p-5">
      <h3 className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Adicionar Tratativa</h3>
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Registrar ação, observação ou tratativa..."
        className="bg-background min-h-[80px] mb-3"
      />
      <Button
        size="sm"
        className="w-full ella-gradient text-white border-0 text-xs"
        disabled={!note.trim() || loading}
        onClick={() => { onSubmit(note.trim()); setNote(""); }}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
        Registrar Tratativa
      </Button>
    </div>
  );
}

function EventImagesGallery({ eventId }: { eventId: number }) {
  const { data: images, isLoading } = trpc.opEvent.getImages.useQuery({ eventId });
  const utils = trpc.useUtils();
  const deleteImage = trpc.opEvent.deleteImage.useMutation({
    onSuccess: () => {
      utils.opEvent.getImages.invalidate({ eventId });
      toast.success("Imagem removida");
    },
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (isLoading) return null;
  if (!images || images.length === 0) return null;

  const originLabel = (origin: string) => {
    switch (origin) {
      case "qr_code": return "QR Code";
      case "agent_camera": return "Câmera Agente";
      case "bodycam": return "Bodycam";
      case "manual": return "Manual";
      default: return origin;
    }
  };

  return (
    <>
      <div className="bg-card border border-border/50 rounded-lg p-5">
        <h3 className="text-xs font-semibold text-muted-foreground mb-4 uppercase tracking-wider flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" />
          Imagens do Evento ({images.length})
        </h3>
        <div className="grid grid-cols-3 gap-2">
          {images.map((img: any) => (
            <div key={img.id} className="relative group">
              <div
                className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border/30 cursor-pointer hover:border-purple-500/50 transition-colors"
                onClick={() => setSelectedImage(img.imageUrl)}
              >
                <img src={img.imageUrl} alt={`Evento #${eventId}`} className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1.5 py-0.5 rounded-b-lg">
                <span className="text-[9px] text-white/80">{originLabel(img.origin)}</span>
              </div>
              <button
                className="absolute top-1 right-1 w-5 h-5 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); deleteImage.mutate({ id: img.id }); }}
              >
                <XCircle className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <img src={selectedImage} alt="Imagem ampliada" className="max-w-full max-h-full object-contain rounded-lg" />
          <button
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20"
            onClick={() => setSelectedImage(null)}
          >
            <XCircle className="w-6 h-6 text-white" />
          </button>
        </div>
      )}
    </>
  );
}
